import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import CloseIcon from '@mui/icons-material/Close';
import useInternalBooking from '../../hooks/useInternalBooking';
import { toast } from 'react-toastify';
import useCardScanner from '../components/useCardScanner.jsx';
import useLasertagGame from '../../hooks/useLasertagGame';

import membercard from '../../assets/membercard.webp';

function ReadMemberCards({
  isOpen,
  onClose,
  booking,
  onMemberAdded,
  linkMode = false,
  linkMemberId = null,
  onCardLinked,
}) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const bookingId = booking?._id;
  const participantCount = booking?.participantCount || 0;
  const assignedMembers = booking?.ltSpecific?.participatingMembers || [];
  const [assignedCount, setAssignedCount] = useState(assignedMembers.length);
  const freeSlotsLocal = Math.max(participantCount - assignedCount, 0);

  const { addParticipantByCardID } = useInternalBooking();
  const { updateMember } = useLasertagGame();

  const [imageLoaded, setImageLoaded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [showCheckmarkAnimation, setShowCheckmarkAnimation] = useState(false);
  const [showCrossAnimation, setShowCrossAnimation] = useState(false);
  const [showInfoAnimation, setShowInfoAnimation] = useState(false);
  const [infoText, setInfoText] = useState('Bereits in dieser Buchung');

  useEffect(() => {
    if (isOpen) setError('');
  }, [isOpen]);

  useEffect(() => {
    // Keep local assigned count in sync when dialog opens or booking changes
    setAssignedCount(assignedMembers.length);
  }, [isOpen, assignedMembers.length]);

  const handleScanComplete = async input => {
    if (!isOpen || !input) return;
    setError('');
    setIsProcessing(true);
    try {
      const cardID = String(input).trim().toUpperCase();
      // link mode: associate scanned card to existing member and close immediately
      if (linkMode && linkMemberId) {
        await updateMember({ memberId: linkMemberId, appendCardID: cardID });
        setShowCheckmarkAnimation(true);
        await new Promise(res => setTimeout(res, 800));
        setShowCheckmarkAnimation(false);
        if (typeof onCardLinked === 'function') onCardLinked({ memberId: linkMemberId, cardID });
        onClose && onClose();
        return;
      }

      if (!bookingId) throw new Error('Keine Buchung ausgewählt');
      const res = await addParticipantByCardID({ bookingId, cardID });

      const alreadyInBooking = res?.code === 'ALREADY_IN_BOOKING';

      if (alreadyInBooking) {
        setInfoText('Bereits in dieser Buchung');
        setShowInfoAnimation(true);
        await new Promise(res => setTimeout(res, 800));
        setShowInfoAnimation(false);
        return; // do not update counters or close
      }

      setShowCheckmarkAnimation(true);
      await new Promise(res => setTimeout(res, 800));
      setShowCheckmarkAnimation(false);

      // Optimistic UI update (increment and propagate to parent)
      const updatedCount = assignedCount + 1;
      setAssignedCount(updatedCount);
      if (typeof onMemberAdded === 'function') {
        const newMember = res?.member || null;
        onMemberAdded(newMember);
      }

      if (updatedCount >= participantCount) {
        onClose && onClose();
      }
    } catch (e) {
      const rawMsg = String(e?.message || '').toLowerCase();
      const errorCode = e?.code || null;
      if (linkMode) {
        // duplicate card -> show blue info
        const isDuplicate =
          errorCode === 'CARD_ID_IN_USE' ||
          rawMsg.includes('already in use') ||
          rawMsg.includes('duplicate') ||
          rawMsg.includes('e11000');
        if (isDuplicate) {
          setInfoText('Bereits anderem Spieler zugeordnet');
          setShowInfoAnimation(true);
          await new Promise(res => setTimeout(res, 800));
          setShowInfoAnimation(false);
          toast.error('Diese Karte ist bereits einem anderen Spieler zugeordnet');
        } else {
          setShowCrossAnimation(true);
          await new Promise(res => setTimeout(res, 800));
          setShowCrossAnimation(false);
          const msg =
            errorCode === 'MEMBER_NOT_FOUND' || rawMsg.includes('not found')
              ? 'Diese Karte ist keinem Spieler zugeordnet'
              : 'Fehler beim Verknüpfen der Karte';
          setError(msg);
          toast.error(msg);
        }
      } else {
        const isAlreadyInBooking =
          errorCode === 'ALREADY_IN_BOOKING' || rawMsg.includes('already part');
        if (isAlreadyInBooking) {
          setInfoText('Bereits in dieser Buchung');
          setShowInfoAnimation(true);
          await new Promise(res => setTimeout(res, 800));
          setShowInfoAnimation(false);
        } else {
          setShowCrossAnimation(true);
          await new Promise(res => setTimeout(res, 800));
          setShowCrossAnimation(false);
          const msg =
            errorCode === 'INVALID_CARD_ID' || rawMsg.includes('invalid cardid')
              ? 'Ungültiges Kartenformat'
              : errorCode === 'MEMBER_NOT_FOUND' || rawMsg.includes('not found')
                ? 'Diese Karte ist keinem Spieler zugeordnet'
                : errorCode === 'NO_FREE_SLOTS' || rawMsg.includes('no free player slots')
                  ? 'Keine freien Slots mehr in der Buchung'
                  : 'Fehler beim Einlesen der Karte';
          setError(msg);
          toast.error(msg);
        }
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const { ScannerInput } = useCardScanner({ enabled: isOpen, onComplete: handleScanComplete });

  useEffect(() => {
    if (!linkMode && isOpen && freeSlotsLocal <= 0) {
      onClose && onClose();
    }
  }, [isOpen, freeSlotsLocal, linkMode]);

  const content = (
    <Grid
      xs={12}
      container
      sx={{ maxWidth: '600px', margin: '0 auto', justifyContent: 'center' }}
      width='95%'
      rowSpacing={1}
    >
      <div style={{ width: '100%' }}>
        <Grid xs={12}>
          <Typography variant='h5' component='h2' align='center' sx={{ mb: 1, color: '#ffffff' }}>
            {linkMode ? 'Karte verknüpfen' : 'Vorhandene Karten einlesen'}
          </Typography>
        </Grid>

        <Grid xs={12}>
          <Typography variant='body2' align='center' sx={{ mb: 1, color: 'text.secondary' }}>
            {linkMode
              ? 'Bitte Karte an das Lesegerät halten.'
              : 'Bitte nacheinander Karten an das Lesegerät halten.'}
          </Typography>
          {!linkMode && (
            <Typography variant='body1' align='center' sx={{ mb: 1, color: 'text.secondary' }}>
              Freie Slots: <b>{freeSlotsLocal}</b>
            </Typography>
          )}
        </Grid>

        {ScannerInput}

        <Grid xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '12px 0',
              width: '356px',
              height: '230px',
              maxWidth: '100%',
              overflow: 'hidden',
            }}
          >
            {!imageLoaded && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isLight ? theme.palette.grey[200] : '#1a1a1a',
                  borderRadius: '12px',
                  zIndex: 1,
                }}
              >
                <CircularProgress size={40} />
              </Box>
            )}
            <img
              src={membercard}
              alt='FunZone Memberkarte'
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '12px',
                boxShadow: '0 8px 32px 0 rgba(60,72,100,0.15)',
                opacity: imageLoaded ? 1 : 0,
                transition: 'opacity 0.3s ease-in-out',
              }}
              onLoad={() => setImageLoaded(true)}
            />

            {showInfoAnimation && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(33, 150, 243, 0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  borderRadius: '12px',
                  animation: 'fadeInOut 0.8s ease-in-out',
                  '@keyframes fadeInOut': {
                    '0%': { opacity: 0, transform: 'scale(0.8)' },
                    '20%': { opacity: 1, transform: 'scale(1)' },
                    '80%': { opacity: 1, transform: 'scale(1)' },
                    '100%': { opacity: 0, transform: 'scale(1.1)' },
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    animation: 'infoPulse 0.8s ease-in-out',
                    '@keyframes infoPulse': {
                      '0%': { transform: 'scale(0) rotate(0deg)' },
                      '30%': { transform: 'scale(1.2) rotate(0deg)' },
                      '70%': { transform: 'scale(1) rotate(0deg)' },
                      '100%': { transform: 'scale(1) rotate(0deg)' },
                    },
                  }}
                >
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    style={{
                      fontSize: '4em',
                      color: '#FFFF00',
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                    }}
                  />
                  <Typography
                    variant='h6'
                    sx={{
                      color: '#FFFF00',
                      fontWeight: 600,
                      marginTop: '8px',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                    }}
                  >
                    {infoText}
                  </Typography>
                </Box>
              </Box>
            )}

            {showCheckmarkAnimation && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(76, 175, 80, 0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  borderRadius: '12px',
                  animation: 'fadeInOut 0.8s ease-in-out',
                  '@keyframes fadeInOut': {
                    '0%': { opacity: 0, transform: 'scale(0.8)' },
                    '20%': { opacity: 1, transform: 'scale(1)' },
                    '80%': { opacity: 1, transform: 'scale(1)' },
                    '100%': { opacity: 0, transform: 'scale(1.1)' },
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    animation: 'checkmarkPulse 0.8s ease-in-out',
                    '@keyframes checkmarkPulse': {
                      '0%': { transform: 'scale(0) rotate(-45deg)' },
                      '30%': { transform: 'scale(1.2) rotate(0deg)' },
                      '70%': { transform: 'scale(1) rotate(0deg)' },
                      '100%': { transform: 'scale(1) rotate(0deg)' },
                    },
                  }}
                >
                  <FontAwesomeIcon
                    icon={faCheck}
                    style={{
                      fontSize: '4em',
                      color: '#FFFF00',
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                    }}
                  />
                  <Typography
                    variant='h6'
                    sx={{
                      color: '#FFFF00',
                      fontWeight: 600,
                      marginTop: '8px',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                    }}
                  >
                    Karte erkannt
                  </Typography>
                </Box>
              </Box>
            )}

            {showCrossAnimation && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(220, 53, 69, 0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  borderRadius: '12px',
                  animation: 'fadeInOut 0.8s ease-in-out',
                  '@keyframes fadeInOut': {
                    '0%': { opacity: 0, transform: 'scale(0.8)' },
                    '20%': { opacity: 1, transform: 'scale(1)' },
                    '80%': { opacity: 1, transform: 'scale(1)' },
                    '100%': { opacity: 0, transform: 'scale(1.1)' },
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    animation: 'crossPulse 0.8s ease-in-out',
                    '@keyframes crossPulse': {
                      '0%': { transform: 'scale(0) rotate(45deg)' },
                      '30%': { transform: 'scale(1.2) rotate(0deg)' },
                      '70%': { transform: 'scale(1) rotate(0deg)' },
                      '100%': { transform: 'scale(1) rotate(0deg)' },
                    },
                  }}
                >
                  <FontAwesomeIcon
                    icon={faTimes}
                    style={{
                      fontSize: '4em',
                      color: '#FFFF00',
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                    }}
                  />
                  <Typography
                    variant='h6'
                    sx={{
                      color: '#FFFF00',
                      fontWeight: 600,
                      marginTop: '8px',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                    }}
                  >
                    Karte nicht erkannt
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>

        {error && (
          <Grid xs={12}>
            <Alert severity='error' sx={{ backgroundColor: '#d32f2f', color: '#ffffff' }}>
              {error}
            </Alert>
          </Grid>
        )}

        <Grid xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            type='button'
            onClick={onClose}
            disabled={isProcessing}
            sx={{ maxWidth: '200px' }}
          >
            {linkMode ? 'Abbrechen' : 'Einlesen beenden'}
          </Button>
        </Grid>
      </div>
    </Grid>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          maxWidth: '600px',
          width: '95%',
          bgcolor: 'background.paper',
          color: 'text.primary',
          position: 'relative',
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'text.secondary',
          zIndex: 1,
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ p: 4, bgcolor: 'background.paper' }}>{content}</DialogContent>
    </Dialog>
  );
}

export default ReadMemberCards;
